import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  ReadOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import {
  AD_POPUP_DELAY_MS,
  AD_POPUP_VISIBLE_MS,
  AD_ROTATION_INTERVAL_MS,
  AD_MAX_PER_DAY,
  IMAGE_URL,
  ROUTE,
  SEEN_ADS_STORAGE_KEY,
} from "../../Constant";
import { useActiveAdvertisementQuery } from "../../Services";
import { Button } from "antd";

interface SeenAdsData {
  date: string;
  ids: string[];
}

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const getSeenData = (): SeenAdsData => {
  try {
    const raw = localStorage.getItem(SEEN_ADS_STORAGE_KEY);
    if (!raw) return { date: getTodayKey(), ids: [] };
    const data = JSON.parse(raw) as SeenAdsData;
    if (data.date !== getTodayKey()) {
      return { date: getTodayKey(), ids: [] };
    }
    return data;
  } catch {
    return { date: getTodayKey(), ids: [] };
  }
};

const markAdSeen = (id: string) => {
  try {
    const data = getSeenData();
    if (!data.ids.includes(id)) {
      data.ids.push(id);
    }
    localStorage.setItem(SEEN_ADS_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable
  }
};

const getSeenIds = (): string[] => getSeenData().ids;

const playNotificationSound = () => {
  try {
    const AudioContextClass =
      window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    const playNote = (
      frequency: number,
      startTime: number,
      duration: number
    ) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(frequency, startTime);

      gain.gain.setValueAtTime(0.15, startTime);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime);
      osc.stop(startTime + duration);
    };

    const now = ctx.currentTime;
    playNote(523.25, now, 0.15); // C5
    playNote(659.25, now + 0.12, 0.25); // E5
  } catch (e) {
    console.warn("Audio context failed to start: ", e);
  }
};

export const AdPopup: React.FC = () => {
  const navigate = useNavigate();
  const { data: ads } = useActiveAdvertisementQuery();
  const [currentAd, setCurrentAd] = useState<any>(null);
  const [visible, setVisible] = useState(false);
  const rotationTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const visibleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delayTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearAllTimers = useCallback(() => {
    if (rotationTimer.current) clearTimeout(rotationTimer.current);
    if (visibleTimer.current) clearTimeout(visibleTimer.current);
    if (delayTimer.current) clearTimeout(delayTimer.current);
    rotationTimer.current = null;
    visibleTimer.current = null;
    delayTimer.current = null;
  }, []);

  const getNextUnseen = useCallback((): any | null => {
    if (!ads || ads.length === 0) return null;

    const seenIds = getSeenIds();

    if (seenIds.length >= AD_MAX_PER_DAY) return null;

    const unseen = ads.filter((ad: any) => !seenIds.includes(ad.id));

    if (unseen.length === 0) return null;

    return unseen[0];
  }, [ads]);

  const showNextAd = useCallback(() => {
    const next = getNextUnseen();
    if (!next) {
      rotationTimer.current = null;
      return;
    }

    delayTimer.current = setTimeout(() => {
      markAdSeen(next.id);
      setCurrentAd(next);
      setVisible(true);
    }, AD_POPUP_DELAY_MS);
  }, [getNextUnseen]);

  const scheduleNext = useCallback(() => {
    if (rotationTimer.current) clearTimeout(rotationTimer.current);
    rotationTimer.current = setTimeout(() => {
      showNextAd();
    }, AD_ROTATION_INTERVAL_MS);
  }, [showNextAd]);

  // Initial ad on load
  useEffect(() => {
    if (!ads || ads.length === 0) return;

    const next = getNextUnseen();
    if (!next) return;

    delayTimer.current = setTimeout(() => {
      markAdSeen(next.id);
      setCurrentAd(next);
      setVisible(true);
    }, AD_POPUP_DELAY_MS);

    return () => {
      if (delayTimer.current) clearTimeout(delayTimer.current);
    };
  }, [ads]);

  // Auto-hide + schedule next after popup is shown
  useEffect(() => {
    if (!visible) return;
    playNotificationSound();

    visibleTimer.current = setTimeout(() => {
      setVisible(false);
      setCurrentAd(null);
      scheduleNext();
    }, AD_POPUP_VISIBLE_MS);

    return () => {
      if (visibleTimer.current) clearTimeout(visibleTimer.current);
    };
  }, [visible, scheduleNext]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearAllTimers();
  }, [clearAllTimers]);

  const handleOpen = () => {
    setVisible(false);
    if (visibleTimer.current) clearTimeout(visibleTimer.current);
    if (currentAd) {
      navigate(`/system/${ROUTE.ad_detail.replace(":ad_id", currentAd.id)}`);
    }
    setCurrentAd(null);
    scheduleNext();
  };

  const handleClose = () => {
    setVisible(false);
    if (visibleTimer.current) clearTimeout(visibleTimer.current);
    setCurrentAd(null);
    scheduleNext();
  };

  if (!currentAd || !visible) return null;

  const imageUrl = currentAd.image_url
    ? `${IMAGE_URL}/${currentAd.image_url}`
    : null;

  const seenCount = getSeenIds().length;
  const totalAds = ads?.length ?? 0;

  return (
    <div
      className="fixed bottom-5 left-5 z-[1000] w-72 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden animate__animated animate__fadeInUp"
      style={{ animationDuration: "0.4s" }}
    >
      <Button
        onClick={handleClose}
        aria-label="Close advertisement"
        icon={<CloseOutlined />}
        className="absolute top-2 right-2 z-10 flex items-center justify-center text-xs"
        type="link"
        size="small"
      />

      {imageUrl && (
        <div className="h-32 w-full bg-gray-100 overflow-hidden">
          <img
            src={imageUrl}
            alt={currentAd.title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="inline-block text-[10px] font-semibold uppercase tracking-wide text-indigo-500 bg-indigo-50 rounded-full px-2 py-0.5">
            {currentAd.type === "product" ? "Product Offer" : "Service Offer"}
          </span>
          {totalAds > 1 && (
            <span className="text-[9px] text-gray-400 font-medium">
              {seenCount}/{totalAds}
            </span>
          )}
        </div>
        <h3 className="m-0 text-sm font-bold text-gray-900 leading-5 line-clamp-2">
          {currentAd.title}
        </h3>
        {currentAd.description && (
          <p className="m-0 mt-1 text-xs text-gray-500 line-clamp-2">
            {currentAd.description}
          </p>
        )}
        <div className="mt-3 flex items-center justify-between gap-2">
          <span className="text-base font-bold text-gray-900">
            {Number(currentAd.price) > 0
              ? `Rs. ${Number(currentAd.price).toLocaleString()}`
              : "View Offer"}
          </span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => navigate(`/system/${ROUTE.ad_list}`)}
              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-indigo-600 border border-gray-300 hover:border-indigo-400 rounded-lg px-2.5 py-1.5 transition-colors"
            >
              <AppstoreOutlined />
              All Ads
            </button>
            <button
              onClick={handleOpen}
              className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5 transition-colors"
            >
              <ReadOutlined />
              View Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
